import { createFileRoute } from "@tanstack/react-router";
import {
  Box,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Hammer,
  MessageCircle,
  Search,
  Send,
  X,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/prototypes/quote")({
  component: QuoteMicrosite,
});

// ─── Mock Data ───────────────────────────────────────────────────────────────

interface LineItem {
  costCode: string;
  title: string;
  description: string;
  quantity: number | null;
  unit: string;
  unitCost: number | null;
  costType: "Labor" | "Material" | "Subcontractor";
  totalCost: number;
}

interface Group {
  id: string;
  name: string;
  items: LineItem[];
  total: number;
}

const GROUPS: Group[] = [
  {
    id: "01",
    name: "Site Preparation",
    items: [
      {
        costCode: "30.00",
        title: "Lead Carpenter",
        description:
          "General site prep, clean up and off haul throughout the course of the project",
        quantity: 8,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 1040,
      },
      {
        costCode: "30.00",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 120,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 10200,
      },
      {
        costCode: "30.00",
        title: "Laborer",
        description: "",
        quantity: 120,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 9000,
      },
    ],
    total: 20240,
  },
  {
    id: "02",
    name: "Demolition",
    items: [
      {
        costCode: "30.01",
        title: "Journeyman Carpenter",
        description: "Miscellaneous pick up demo",
        quantity: 24,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 2640,
      },
      {
        costCode: "30.01",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 24,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 2040,
      },
      {
        costCode: "30.01",
        title: "Laborer",
        description: "",
        quantity: 24,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 1800,
      },
    ],
    total: 6480,
  },
  {
    id: "04",
    name: "Carpentry - Rough",
    items: [
      {
        costCode: "32.00",
        title: "Journeyman Carpenter",
        description:
          "Shower stalls, bench seats, niches, medicine cabinets, pocket door cages, and backing",
        quantity: 56,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 6160,
      },
      {
        costCode: "32.00",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 56,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 4760,
      },
      {
        costCode: "32.00",
        title: "Laborer",
        description: "",
        quantity: 16,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 1200,
      },
    ],
    total: 12120,
  },
  {
    id: "10a",
    name: "Carpentry - Interior Doors",
    items: [
      {
        costCode: "34.11",
        title: "Lead Carpenter",
        description: "Install of 14 new door units (doubles counting as 1)",
        quantity: 40,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 5200,
      },
      {
        costCode: "34.11",
        title: "Journeyman Carpenter",
        description: "",
        quantity: 40,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 4400,
      },
      {
        costCode: "34.40",
        title: "Journeyman Carpenter",
        description:
          "Install of standard bore hardware for interior doors and flush pulls for pocket doors",
        quantity: 16,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 1760,
      },
    ],
    total: 11360,
  },
  {
    id: "10b",
    name: "Carpentry - Exterior Doors",
    items: [
      {
        costCode: "34.11",
        title: "Lead Carpenter",
        description: "Install of exterior doors per plan",
        quantity: 40,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 5200,
      },
      {
        costCode: "34.11",
        title: "Journeyman Carpenter",
        description: "",
        quantity: 32,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 3520,
      },
      {
        costCode: "34.11",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 8,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 680,
      },
      {
        costCode: "34.11",
        title: "Laborer",
        description: "",
        quantity: 8,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 600,
      },
      {
        costCode: "34.40",
        title: "Journeyman Carpenter",
        description:
          "Install of frontdoor hardware and Marvin hardware for exterior doors and front door",
        quantity: 24,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 2640,
      },
    ],
    total: 12640,
  },
  {
    id: "12",
    name: "Carpentry - Exterior Finish",
    items: [
      {
        costCode: "34.31",
        title: "Lead Carpenter",
        description:
          "Shingle mold, window sills, wrapped posts for trellis, trellis system and exterior of sun room",
        quantity: 220,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 28600,
      },
      {
        costCode: "34.31",
        title: "Journeyman Carpenter",
        description: "",
        quantity: 220,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 24200,
      },
      {
        costCode: "34.31",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 110,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 9350,
      },
      {
        costCode: "34.31",
        title: "Laborer",
        description: "",
        quantity: 110,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 8250,
      },
    ],
    total: 70400,
  },
  {
    id: "11",
    name: "Carpentry - Interior Finish",
    items: [
      {
        costCode: "34.30",
        title: "Lead Carpenter",
        description: "Crown, base, case and wainscot per spec sheet",
        quantity: 240,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 31200,
      },
      {
        costCode: "34.30",
        title: "Journeyman Carpenter",
        description: "",
        quantity: 240,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 26400,
      },
      {
        costCode: "34.30",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 240,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 20400,
      },
      {
        costCode: "34.30",
        title: "Laborer",
        description: "",
        quantity: 40,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 3000,
      },
    ],
    total: 81000,
  },
  {
    id: "08",
    name: "Cabinet Installation",
    items: [
      {
        costCode: "34.00",
        title: "Lead Carpenter",
        description:
          "Install of cabinetry as shown on plan for family room, kitchen, pantry, coffee bar, butler bar, 4 bathroom vanities, mudroom, primary bedroom & primary closet",
        quantity: 120,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 15600,
      },
      {
        costCode: "34.00",
        title: "Journeyman Carpenter",
        description: "",
        quantity: 120,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 13200,
      },
      {
        costCode: "34.00",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 80,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 6800,
      },
      {
        costCode: "34.00",
        title: "Laborer",
        description: "",
        quantity: 80,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 6000,
      },
      {
        costCode: "34.40",
        title: "Journeyman Carpenter",
        description:
          "Install of all hardware for cabinets that are included in the quote",
        quantity: 32,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 3520,
      },
    ],
    total: 45120,
  },
  {
    id: "08w",
    name: "Window Installation",
    items: [
      {
        costCode: "34.20",
        title: "Lead Carpenter",
        description: "Install of all windows per plan on first floor",
        quantity: 120,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 15600,
      },
      {
        costCode: "34.20",
        title: "Journeyman Carpenter",
        description: "",
        quantity: 120,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 13200,
      },
      {
        costCode: "34.20",
        title: "2nd Grade Carpenter",
        description: "",
        quantity: 80,
        unit: "hours",
        unitCost: 85,
        costType: "Labor",
        totalCost: 6800,
      },
      {
        costCode: "34.20",
        title: "Laborer",
        description: "",
        quantity: 80,
        unit: "hours",
        unitCost: 75,
        costType: "Labor",
        totalCost: 6000,
      },
    ],
    total: 41600,
  },
  {
    id: "05",
    name: "Plumbing Demo",
    items: [
      {
        costCode: "33.00",
        title: "Lead Plumber",
        description: "",
        quantity: 30,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 3900,
      },
      {
        costCode: "33.00",
        title: "Journeyman Plumber",
        description: "",
        quantity: 30,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 3300,
      },
    ],
    total: 7200,
  },
  {
    id: "06",
    name: "Plumbing Rough",
    items: [
      {
        costCode: "33.01",
        title: "Lead Plumber",
        description: "",
        quantity: 325,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 42250,
      },
      {
        costCode: "33.01",
        title: "Journeyman Plumber",
        description: "",
        quantity: 325,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 35750,
      },
    ],
    total: 78000,
  },
  {
    id: "07",
    name: "Plumbing Finish",
    items: [
      {
        costCode: "33.02",
        title: "Lead Plumber",
        description: "",
        quantity: 74,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 9620,
      },
      {
        costCode: "33.02",
        title: "Journeyman Plumber",
        description: "",
        quantity: 74,
        unit: "hours",
        unitCost: 110,
        costType: "Labor",
        totalCost: 8140,
      },
    ],
    total: 17760,
  },
  {
    id: "14",
    name: "Appliance Install",
    items: [
      {
        costCode: "36.00",
        title: "Lead Carpenter",
        description: "",
        quantity: 8,
        unit: "hours",
        unitCost: 130,
        costType: "Labor",
        totalCost: 1040,
      },
    ],
    total: 1040,
  },
  {
    id: "18",
    name: "Project Management",
    items: [
      {
        costCode: "20.03",
        title: "Project Manager",
        description: "",
        quantity: 150,
        unit: "hours",
        unitCost: 200,
        costType: "Labor",
        totalCost: 30000,
      },
      {
        costCode: "21.00",
        title: "General Contractor",
        description: "",
        quantity: 25,
        unit: "hours",
        unitCost: 250,
        costType: "Labor",
        totalCost: 6250,
      },
      {
        costCode: "21.00",
        title: "Superintendent",
        description: "",
        quantity: 200,
        unit: "hours",
        unitCost: 200,
        costType: "Labor",
        totalCost: 40000,
      },
    ],
    total: 76250,
  },
  {
    id: "15",
    name: "Materials",
    items: [
      {
        costCode: "39.00",
        title: "Demo",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 850,
        costType: "Material",
        totalCost: 850,
      },
      {
        costCode: "39.00",
        title: "Site Prep",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 2500,
        costType: "Material",
        totalCost: 2500,
      },
      {
        costCode: "32.50",
        title: "Lumber",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 2000,
        costType: "Material",
        totalCost: 2000,
      },
      {
        costCode: "34.51",
        title: "Exterior Finish Trim",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 28500,
        costType: "Material",
        totalCost: 28500,
      },
      {
        costCode: "33.50",
        title: "Interior Finish Trim",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 27000,
        costType: "Material",
        totalCost: 27000,
      },
      {
        costCode: "39.00",
        title: "Misc - Cabinet Install",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 950,
        costType: "Material",
        totalCost: 950,
      },
      {
        costCode: "39.00",
        title: "Misc - Window Install",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 1500,
        costType: "Material",
        totalCost: 1500,
      },
      {
        costCode: "39.00",
        title: "Misc - Plumbing Demo",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 400,
        costType: "Material",
        totalCost: 400,
      },
      {
        costCode: "33.50",
        title: "Rough Plumbing",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 20000,
        costType: "Material",
        totalCost: 20000,
      },
      {
        costCode: "33.51",
        title: "Finish Plumbing",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 1300,
        costType: "Material",
        totalCost: 1300,
      },
    ],
    total: 85000,
  },
  {
    id: "19",
    name: "Subcontractors",
    items: [
      {
        costCode: "",
        title: "Appliance Installation",
        description: "Allowance based on similar project",
        quantity: 1,
        unit: "LS",
        unitCost: 6000,
        costType: "Subcontractor",
        totalCost: 6000,
      },
      {
        costCode: "",
        title: "Audio Video (Woodland AV)",
        description:
          "Adjusted for first floor only. House network and wiring budget only",
        quantity: 1,
        unit: "LS",
        unitCost: 18668,
        costType: "Subcontractor",
        totalCost: 18668,
      },
      {
        costCode: "",
        title: "Cabinet Maker (The Brand)",
        description:
          "All cabinetry for the first floor, less the primary closet",
        quantity: 1,
        unit: "LS",
        unitCost: 177381,
        costType: "Subcontractor",
        totalCost: 177381,
      },
      {
        costCode: "",
        title: "Cleaning Post Remodel (Hygena)",
        description: "Allowance",
        quantity: 1,
        unit: "LS",
        unitCost: 3000,
        costType: "Subcontractor",
        totalCost: 3000,
      },
      {
        costCode: "",
        title: "Demolition (TNT Demo)",
        description: "Alternate B removed (second floor demo)",
        quantity: 1,
        unit: "LS",
        unitCost: 112852,
        costType: "Subcontractor",
        totalCost: 112852,
      },
      {
        costCode: "",
        title: "Asbestos & Lead Abatement (TNT Demo)",
        description:
          "Abatement work for removal of asbestos and lead per report provided",
        quantity: 1,
        unit: "LS",
        unitCost: 34268,
        costType: "Subcontractor",
        totalCost: 34268,
      },
      {
        costCode: "",
        title: "Interior Doors (Belmont Pioneer)",
        description: "New interior doors per plan for the first floor",
        quantity: 1,
        unit: "LS",
        unitCost: 26928,
        costType: "Subcontractor",
        totalCost: 26928,
      },
      {
        costCode: "",
        title: "Entry Door (Belmont Pioneer)",
        description: "New entry door per plan",
        quantity: 1,
        unit: "LS",
        unitCost: 22443,
        costType: "Subcontractor",
        totalCost: 22443,
      },
      {
        costCode: "",
        title: "Drywall (Allen Drywall)",
        description: "Cost includes level #5 upgrade ($13,975)",
        quantity: 1,
        unit: "LS",
        unitCost: 89165,
        costType: "Subcontractor",
        totalCost: 89165,
      },
      {
        costCode: "",
        title: "Electrical (Conoley)",
        description: "Cost included only outlines first floor scope",
        quantity: 1,
        unit: "LS",
        unitCost: 186600,
        costType: "Subcontractor",
        totalCost: 186600,
      },
      {
        costCode: "",
        title: "Fire Suppression (Adanac Fire)",
        description:
          "Fire suppression throughout the house (1st and second floor)",
        quantity: 1,
        unit: "LS",
        unitCost: 31900,
        costType: "Subcontractor",
        totalCost: 31900,
      },
      {
        costCode: "",
        title: "Fireplaces (Blaze)",
        description:
          "Town and Country Fireplaces quoted per plan for the living room only",
        quantity: 1,
        unit: "LS",
        unitCost: 19880,
        costType: "Subcontractor",
        totalCost: 19880,
      },
      {
        costCode: "",
        title: "Hardwood Floors (Allwood Floors)",
        description:
          "Installation of prefinished floor (1st floor). Material included.",
        quantity: 1,
        unit: "LS",
        unitCost: 104654,
        costType: "Subcontractor",
        totalCost: 104654,
      },
      {
        costCode: "",
        title: "Foundation/Concrete (JF Ohlund)",
        description: "JF Ohlund quote for foundation work",
        quantity: 1,
        unit: "LS",
        unitCost: 202100,
        costType: "Subcontractor",
        totalCost: 202100,
      },
      {
        costCode: "",
        title: "Framing (JF Ohlund)",
        description:
          "All structural framing and wall framing at first floor only",
        quantity: 1,
        unit: "LS",
        unitCost: 221400,
        costType: "Subcontractor",
        totalCost: 221400,
      },
      {
        costCode: "",
        title: "Gypcrete (Flatline Floor)",
        description:
          "Gypcrete at sunroom. Allowance used based on similar project",
        quantity: 1,
        unit: "LS",
        unitCost: 3500,
        costType: "Subcontractor",
        totalCost: 3500,
      },
      {
        costCode: "",
        title: "HVAC & Sheet Metal (Sandium)",
        description:
          "Cost included for the Carrier system for the first floor. Options to upgrade to Mitsubishi system",
        quantity: 1,
        unit: "LS",
        unitCost: 51000,
        costType: "Subcontractor",
        totalCost: 51000,
      },
      {
        costCode: "",
        title: "Insulation (SDI)",
        description:
          "Allowance for code required insulation for thermal envelope at first floor",
        quantity: 1,
        unit: "LS",
        unitCost: 12000,
        costType: "Subcontractor",
        totalCost: 12000,
      },
      {
        costCode: "",
        title: "Painting (Eddie Perez)",
        description: "Painting for the first floor and complete exterior",
        quantity: 1,
        unit: "LS",
        unitCost: 245100,
        costType: "Subcontractor",
        totalCost: 245100,
      },
      {
        costCode: "",
        title: "Plaster (Willem Racke)",
        description:
          "Allowance for plaster hood based on similar projects. Final pricing subject to change",
        quantity: 1,
        unit: "LS",
        unitCost: 4500,
        costType: "Subcontractor",
        totalCost: 4500,
      },
      {
        costCode: "",
        title: "Roofing (MidPen)",
        description: "Roofing quote expected Monday 10/6/25",
        quantity: 1,
        unit: "LS",
        unitCost: 6725,
        costType: "Subcontractor",
        totalCost: 6725,
      },
      {
        costCode: "",
        title: "Sanitation (American Portables)",
        description: "",
        quantity: 12,
        unit: "LS",
        unitCost: 350,
        costType: "Subcontractor",
        totalCost: 4200,
      },
      {
        costCode: "",
        title: "Scaffolding (R&R Scaffolding)",
        description: "Allowance based on similar projects",
        quantity: 1,
        unit: "LS",
        unitCost: 15000,
        costType: "Subcontractor",
        totalCost: 15000,
      },
      {
        costCode: "",
        title: "Sheet Metal Fabrication",
        description:
          "Allowance for sheet metal flashings for trellis and new windows",
        quantity: 1,
        unit: "LS",
        unitCost: 15000,
        costType: "Subcontractor",
        totalCost: 15000,
      },
      {
        costCode: "",
        title: "Shower Enclosures (CB Showers)",
        description:
          "Shower enclosures for first floor. Additional options and costs for low-e coating and Enduroshield",
        quantity: 1,
        unit: "LS",
        unitCost: 9495,
        costType: "Subcontractor",
        totalCost: 9495,
      },
      {
        costCode: "",
        title: "Tilework (JB Tile and Stone)",
        description:
          "Labor for fabrication and installation only. Tile and stone material provided by TRG",
        quantity: 1,
        unit: "LS",
        unitCost: 84732,
        costType: "Subcontractor",
        totalCost: 84732,
      },
      {
        costCode: "",
        title: "Underground Water Service",
        description: "Allowance based on similar projects",
        quantity: 1,
        unit: "LS",
        unitCost: 30000,
        costType: "Subcontractor",
        totalCost: 30000,
      },
      {
        costCode: "",
        title: "Waterproofing (Renaissance)",
        description:
          "Waterproofing at sunroom floor below gypcrete. $12/sq ft x 400 sq ft",
        quantity: 1,
        unit: "LS",
        unitCost: 2400,
        costType: "Subcontractor",
        totalCost: 2400,
      },
      {
        costCode: "",
        title: "Waste Disposal (Blueline)",
        description: "Dump fees for debris off haul",
        quantity: 12,
        unit: "QTY",
        unitCost: 350,
        costType: "Subcontractor",
        totalCost: 4200,
      },
      {
        costCode: "",
        title: "Window Washing (Associated Window Cleaners)",
        description: "Allowance based on similar projects",
        quantity: 1,
        unit: "LS",
        unitCost: 2500,
        costType: "Subcontractor",
        totalCost: 2500,
      },
      {
        costCode: "",
        title: "Skylights (Velux)",
        description: "New skylights for sunroom",
        quantity: 1,
        unit: "LS",
        unitCost: 16700,
        costType: "Subcontractor",
        totalCost: 16700,
      },
      {
        costCode: "",
        title: "Windows & Exterior Doors (Marvin)",
        description: "",
        quantity: 1,
        unit: "LS",
        unitCost: 197257,
        costType: "Subcontractor",
        totalCost: 197257,
      },
      {
        costCode: "",
        title: "Bluestone Pedestal Paver System (Truscapes)",
        description:
          "Allowance for bluestone pavers and pedestal system. Approx 1200 sq ft @ $70/sq ft",
        quantity: 1,
        unit: "LS",
        unitCost: 84000,
        costType: "Subcontractor",
        totalCost: 84000,
      },
      {
        costCode: "",
        title: "Sidewall Shingles (Midpeninsula Roofing)",
        description: "New sidewall shingles at entire first floor",
        quantity: 1,
        unit: "LS",
        unitCost: 115000,
        costType: "Subcontractor",
        totalCost: 115000,
      },
    ],
    total: 2160548,
  },
];

const SUBTOTALS = {
  jplMaterial: 85000,
  jplLabor: 480170,
  subcontractor: 2160548,
  materialMarkup: 29750,
  subcontractorMarkup: 334885,
  grandTotal: 3090353,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

type View = "summary" | "model" | "own-work";

// ─── Chat Types ──────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
}

const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    text: "Hi! I can help you understand this estimate for 175 Pepper Ave. Ask me about any line item, cost breakdown, subcontractor, or how costs compare to typical projects.",
  },
];

// ─── Main Component ──────────────────────────────────────────────────────────

function QuoteMicrosite() {
  const [view, setView] = useState<View>("summary");
  const [chatOpen, setChatOpen] = useState(true);

  return (
    <SidebarProvider defaultOpen>
      <QuoteSidebar view={view} setView={setView} />

      {/* Main content area */}
      <SidebarInset className="flex min-w-0 flex-1 flex-col !min-h-0 h-svh">
        <QuoteHeader
          chatOpen={chatOpen}
          onToggleChat={() => setChatOpen(!chatOpen)}
        />
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-auto">
            {view === "summary" && <SummaryView />}
            {view === "model" && <ModelView />}
            {view === "own-work" && <OwnWorkView />}
          </div>
          {chatOpen && (
            <div className="w-96 border-l">
              <ChatPane onClose={() => setChatOpen(false)} />
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function QuoteSidebar({
  view,
  setView,
}: {
  view: View;
  setView: (v: View) => void;
}) {
  const { isMobile, setOpenMobile } = useSidebar();

  const selectView = useCallback(
    (v: View) => {
      setView(v);
      if (isMobile) setOpenMobile(false);
    },
    [isMobile, setView, setOpenMobile],
  );

  return (
    <Sidebar variant="sidebar" collapsible="offcanvas" className="w-56">
      <SidebarHeader className="border-b px-4 py-3">
        <div className="text-sm font-semibold">175 Pepper Ave</div>
        <div className="text-xs text-muted-foreground">JP Lindstrom, Inc</div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={view === "summary"}
                onClick={() => selectView("summary")}
                tooltip="Summary"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Summary
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={view === "model"}
                onClick={() => selectView("model")}
                tooltip="Model"
              >
                <Box className="h-4 w-4" />
                Model
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={view === "own-work"}
                onClick={() => selectView("own-work")}
                tooltip="Own Work"
              >
                <Hammer className="h-4 w-4" />
                Own Work
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function QuoteHeader({
  chatOpen,
  onToggleChat,
}: {
  chatOpen: boolean;
  onToggleChat: () => void;
}) {
  return (
    <header className="flex items-start justify-between border-b px-4 py-4 sm:px-6">
      <div className="flex items-start gap-3">
        <SidebarTrigger className="mt-1 border md:hidden" />
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            175 Pepper Ave
          </h1>
          <p className="text-sm text-muted-foreground">
            Client: Bill and Stacy Farner &middot; Architect: TRG &middot;
            Proposal Date: 10/03/25
          </p>
          <p className="text-xs text-muted-foreground">
            Prepared by Jake Lindstrom &middot; jake@jplindstrom.com &middot;
            (650) 773-6159
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Estimate Total
          </div>
          <div className="text-3xl font-bold tabular-nums tracking-tight">
            {fmt(SUBTOTALS.grandTotal)}
          </div>
        </div>
        <Button
          variant={chatOpen ? "secondary" : "outline"}
          size="icon"
          onClick={onToggleChat}
          className="ml-2 mr-1 shrink-0"
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}

// ─── Pie Chart ───────────────────────────────────────────────────────────────

const DIVISION_COLORS = [
  "#3b82f6",
  "#ef4444",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#6366f1",
  "#14b8a6",
  "#e11d48",
  "#a855f7",
  "#eab308",
  "#0ea5e9",
  "#d946ef",
  "#22c55e",
  "#f43f5e",
  "#a3e635",
  "#2dd4bf",
  "#818cf8",
  "#fb923c",
  "#c084fc",
  "#fbbf24",
  "#38bdf8",
  "#4ade80",
  "#f472b6",
  "#34d399",
  "#facc15",
  "#7c3aed",
];

interface DivisionSlice {
  name: string;
  total: number;
  color: string;
  percent: number;
}

const BREAKOUT_THRESHOLD = 33;

function useDivisionBreakdown(): DivisionSlice[] {
  return useMemo(() => {
    const grandTotal = GROUPS.reduce((s, g) => s + g.total, 0);
    const raw: { name: string; total: number }[] = [];

    for (const g of GROUPS) {
      const pct = grandTotal > 0 ? (g.total / grandTotal) * 100 : 0;
      if (pct > BREAKOUT_THRESHOLD) {
        // Explode into individual line items
        for (const item of g.items) {
          raw.push({ name: item.title, total: item.totalCost });
        }
      } else {
        raw.push({ name: g.name, total: g.total });
      }
    }

    return raw
      .sort((a, b) => b.total - a.total)
      .map((s, i) => ({
        ...s,
        color: DIVISION_COLORS[i % DIVISION_COLORS.length],
        percent: grandTotal > 0 ? (s.total / grandTotal) * 100 : 0,
      }));
  }, []);
}

function DonutChart({
  slices,
  size = 220,
  hoveredIndex,
  onHover,
}: {
  slices: DivisionSlice[];
  size?: number;
  hoveredIndex: number | null;
  onHover: (i: number | null) => void;
}) {
  const pad = 6;
  const r = size / 2;
  const stroke = size * 0.15;
  const cr = r - stroke / 2;
  const circumference = 2 * Math.PI * cr;

  let offset = 0;
  const arcs = slices.map((s, i) => {
    const len = (s.percent / 100) * circumference;
    const arc = { ...s, dashOffset: offset, dashLen: len, index: i };
    offset += len;
    return arc;
  });

  const center = hoveredIndex != null ? slices[hoveredIndex] : null;
  const vb = -pad;
  const vbSize = size + pad * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`${vb} ${vb} ${vbSize} ${vbSize}`}
      className="shrink-0 overflow-visible"
    >
      {arcs.map((arc) => (
        <circle
          key={arc.index}
          cx={r}
          cy={r}
          r={cr}
          fill="none"
          stroke={arc.color}
          strokeWidth={hoveredIndex === arc.index ? stroke + 6 : stroke}
          strokeDasharray={`${arc.dashLen} ${circumference - arc.dashLen}`}
          strokeDashoffset={-arc.dashOffset}
          opacity={hoveredIndex != null && hoveredIndex !== arc.index ? 0.4 : 1}
          className="transition-all duration-150"
          style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
          onMouseEnter={() => onHover(arc.index)}
          onMouseLeave={() => onHover(null)}
        />
      ))}
      {center ? (
        <>
          <text
            x={r}
            y={r - 12}
            textAnchor="middle"
            className="fill-foreground text-sm font-semibold"
          >
            {center.percent.toFixed(1)}%
          </text>
          <text
            x={r}
            y={r + 4}
            textAnchor="middle"
            className="fill-muted-foreground text-[10px]"
          >
            {center.name.length > 18
              ? center.name.slice(0, 16) + "..."
              : center.name}
          </text>
          <text
            x={r}
            y={r + 18}
            textAnchor="middle"
            className="fill-foreground text-[11px] font-medium"
          >
            {fmt(center.total)}
          </text>
        </>
      ) : (
        <>
          <text
            x={r}
            y={r - 4}
            textAnchor="middle"
            className="fill-foreground text-lg font-bold"
          >
            {fmt(SUBTOTALS.grandTotal)}
          </text>
          <text
            x={r}
            y={r + 14}
            textAnchor="middle"
            className="fill-muted-foreground text-[10px]"
          >
            Total
          </text>
        </>
      )}
    </svg>
  );
}

const LEGEND_DEFAULT_COUNT = 8;

function CostBreakdownChart() {
  const slices = useDivisionBreakdown();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [legendExpanded, setLegendExpanded] = useState(false);

  const visibleSlices = legendExpanded
    ? slices
    : slices.slice(0, LEGEND_DEFAULT_COUNT);
  const hiddenCount = slices.length - LEGEND_DEFAULT_COUNT;

  return (
    <div className="flex flex-col items-center gap-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Cost by Division
      </h3>
      <DonutChart
        slices={slices}
        hoveredIndex={hoveredIndex}
        onHover={setHoveredIndex}
      />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        {visibleSlices.map((s, i) => {
          const realIndex = legendExpanded ? i : slices.indexOf(s);
          return (
            <div
              key={s.name}
              className={cn(
                "flex cursor-default items-center gap-1.5 rounded px-1 py-0.5 transition-colors",
                hoveredIndex === realIndex && "bg-muted",
              )}
              onMouseEnter={() => setHoveredIndex(realIndex)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: s.color }}
              />
              <span className="truncate text-muted-foreground">{s.name}</span>
              <span className="ml-auto font-medium tabular-nums">
                {s.percent.toFixed(0)}%
              </span>
            </div>
          );
        })}
        {hiddenCount > 0 && (
          <button
            className="col-span-2 cursor-pointer text-left text-muted-foreground hover:text-foreground"
            onClick={() => setLegendExpanded(!legendExpanded)}
          >
            {legendExpanded ? "Show less" : `+ ${hiddenCount} more`}
          </button>
        )}
      </div>
    </div>
  );
}

function ModelPreview() {
  return (
    <div className="flex flex-col items-center gap-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Building Model
      </h3>
      <div className="flex h-[280px] w-full items-center justify-center rounded-lg border border-dashed bg-muted/30">
        <div className="text-center">
          <Box className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <p className="mt-2 text-sm text-muted-foreground">
            Interactive 3D model
          </p>
          <p className="text-xs text-muted-foreground">
            Bear viewer will render here
          </p>
        </div>
      </div>
    </div>
  );
}

function SummaryHero() {
  return (
    <div className="mb-6 grid grid-cols-1 gap-8 md:grid-cols-2">
      <CostBreakdownChart />
      <ModelPreview />
    </div>
  );
}

// ─── Summary View ────────────────────────────────────────────────────────────

function SummaryView() {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [costTypeFilter, setCostTypeFilter] = useState<string | null>(null);

  const toggleGroup = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filteredGroups = useMemo(() => {
    const q = search.toLowerCase();
    return GROUPS.map((g) => {
      const items = g.items.filter((item) => {
        const matchesSearch =
          !q ||
          item.title.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q) ||
          item.costCode.toLowerCase().includes(q) ||
          g.name.toLowerCase().includes(q);
        const matchesCostType =
          !costTypeFilter || item.costType === costTypeFilter;
        return matchesSearch && matchesCostType;
      });
      return { ...g, items, total: items.reduce((s, i) => s + i.totalCost, 0) };
    }).filter((g) => g.items.length > 0);
  }, [search, costTypeFilter]);

  const filteredTotal = filteredGroups.reduce((s, g) => s + g.total, 0);

  return (
    <div className="p-6">
      <SummaryHero />

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search line items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1.5">
          {(["Labor", "Material", "Subcontractor"] as const).map((type) => (
            <Badge
              key={type}
              variant={costTypeFilter === type ? "default" : "outline"}
              className="cursor-pointer select-none"
              onClick={() =>
                setCostTypeFilter(costTypeFilter === type ? null : type)
              }
            >
              {type}
            </Badge>
          ))}
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Group</TableHead>
              <TableHead className="hidden md:table-cell">Cost Code</TableHead>
              <TableHead className="hidden lg:table-cell">
                Description
              </TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="hidden sm:table-cell">Unit</TableHead>
              <TableHead className="text-right">Unit Cost</TableHead>
              <TableHead className="hidden sm:table-cell">Type</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredGroups.map((group) => (
              <GroupRows
                key={group.id}
                group={group}
                expanded={expanded.has(group.id)}
                onToggle={() => toggleGroup(group.id)}
              />
            ))}
            {/* Filtered total */}
            <TableRow className="border-t-2 bg-muted/50 font-semibold">
              <TableCell />
              <TableCell>
                {costTypeFilter || search
                  ? "Filtered Total"
                  : "Subtotal (All Groups)"}
              </TableCell>
              <TableCell className="hidden md:table-cell" />
              <TableCell className="hidden lg:table-cell" />
              <TableCell />
              <TableCell className="hidden sm:table-cell" />
              <TableCell />
              <TableCell className="hidden sm:table-cell" />
              <TableCell className="text-right tabular-nums">
                {fmt(filteredTotal)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Summary footer */}
      <div className="mt-6 flex justify-end">
        <div className="w-80 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">JPL Material</span>
            <span className="tabular-nums">{fmt(SUBTOTALS.jplMaterial)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">JPL Labor</span>
            <span className="tabular-nums">{fmt(SUBTOTALS.jplLabor)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Subcontractor</span>
            <span className="tabular-nums">{fmt(SUBTOTALS.subcontractor)}</span>
          </div>
          <div className="my-1 border-t" />
          <div className="flex justify-between">
            <span className="text-muted-foreground">Material Markup (35%)</span>
            <span className="tabular-nums">
              {fmt(SUBTOTALS.materialMarkup)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              Subcontractor Markup (15.5%)
            </span>
            <span className="tabular-nums">
              {fmt(SUBTOTALS.subcontractorMarkup)}
            </span>
          </div>
          <div className="my-1 border-t-2" />
          <div className="flex justify-between text-base font-bold">
            <span>Grand Total</span>
            <span className="tabular-nums">{fmt(SUBTOTALS.grandTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupRows({
  group,
  expanded,
  onToggle,
}: {
  group: Group;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      {/* Group header row */}
      <TableRow
        className="cursor-pointer bg-muted/30 hover:bg-muted/50"
        onClick={onToggle}
      >
        <TableCell className="w-8 px-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </TableCell>
        <TableCell className="font-medium">{group.name}</TableCell>
        <TableCell className="hidden md:table-cell" />
        <TableCell className="hidden lg:table-cell text-muted-foreground text-xs">
          {group.items.length} line item{group.items.length !== 1 ? "s" : ""}
        </TableCell>
        <TableCell />
        <TableCell className="hidden sm:table-cell" />
        <TableCell />
        <TableCell className="hidden sm:table-cell" />
        <TableCell className="text-right font-medium tabular-nums">
          {fmt(group.total)}
        </TableCell>
      </TableRow>
      {/* Line items */}
      {expanded &&
        group.items.map((item, i) => (
          <TableRow key={`${group.id}-${i}`} className="text-sm">
            <TableCell />
            <TableCell className="pl-6 text-muted-foreground">
              {item.title}
            </TableCell>
            <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">
              {item.costCode}
            </TableCell>
            <TableCell className="hidden lg:table-cell max-w-xs truncate text-xs text-muted-foreground">
              {item.description}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {item.quantity ?? ""}
            </TableCell>
            <TableCell className="hidden sm:table-cell text-muted-foreground">
              {item.unit}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {item.unitCost ? fmt(item.unitCost) : ""}
            </TableCell>
            <TableCell className="hidden sm:table-cell">
              <CostTypeBadge type={item.costType} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {fmt(item.totalCost)}
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}

function CostTypeBadge({ type }: { type: string }) {
  const variant =
    type === "Labor"
      ? "secondary"
      : type === "Material"
        ? "outline"
        : "default";
  return (
    <Badge variant={variant} className="text-[10px]">
      {type}
    </Badge>
  );
}

// ─── Model View ──────────────────────────────────────────────────────────────

function ModelView() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <Box className="h-16 w-16 text-muted-foreground/30" />
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">3D Building Model</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          The interactive 3D model for 175 Pepper Ave will render here. This
          will use the Bear viewer to let you explore the building and
          cross-reference estimate line items with their physical locations.
        </p>
      </div>
    </div>
  );
}

// ─── Own Work View ───────────────────────────────────────────────────────────

function OwnWorkView() {
  const [search, setSearch] = useState("");

  const ownWorkGroups = useMemo(() => {
    const q = search.toLowerCase();
    return GROUPS.filter((g) => g.id !== "19") // exclude subcontractors
      .map((g) => {
        const items = g.items.filter(
          (item) =>
            item.costType !== "Subcontractor" &&
            (!q ||
              item.title.toLowerCase().includes(q) ||
              item.description.toLowerCase().includes(q) ||
              g.name.toLowerCase().includes(q)),
        );
        return {
          ...g,
          items,
          total: items.reduce((s, i) => s + i.totalCost, 0),
        };
      })
      .filter((g) => g.items.length > 0);
  }, [search]);

  // Split into labor and material
  const laborGroups = ownWorkGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => i.costType === "Labor"),
    }))
    .map((g) => ({
      ...g,
      total: g.items.reduce((s, i) => s + i.totalCost, 0),
    }))
    .filter((g) => g.items.length > 0);

  const materialGroups = ownWorkGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => i.costType === "Material"),
    }))
    .map((g) => ({
      ...g,
      total: g.items.reduce((s, i) => s + i.totalCost, 0),
    }))
    .filter((g) => g.items.length > 0);

  const laborTotal = laborGroups.reduce((s, g) => s + g.total, 0);
  const materialTotal = materialGroups.reduce((s, g) => s + g.total, 0);

  // Aggregate labor hours by role
  const laborByRole = useMemo(() => {
    const map = new Map<string, { hours: number; cost: number }>();
    for (const g of laborGroups) {
      for (const item of g.items) {
        const existing = map.get(item.title) ?? { hours: 0, cost: 0 };
        existing.hours += item.quantity ?? 0;
        existing.cost += item.totalCost;
        map.set(item.title, existing);
      }
    }
    return [...map.entries()].sort((a, b) => b[1].cost - a[1].cost);
  }, [laborGroups]);

  return (
    <div className="space-y-8 p-6">
      <div>
        <h2 className="text-xl font-semibold">Own Work Breakdown</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Detailed view of JPL labor and material costs (excludes
          subcontractors)
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search own work..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Labor summary cards */}
      <div>
        <h3 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Labor by Role
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {laborByRole.map(([role, data]) => (
            <div
              key={role}
              className="rounded-lg border bg-card p-4 text-card-foreground"
            >
              <div className="text-sm font-medium">{role}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {fmt(data.cost)}
              </div>
              <div className="text-xs text-muted-foreground">
                {data.hours.toLocaleString()} hours
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Labor detail table */}
      <div>
        <h3 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Labor Detail ({fmt(laborTotal)})
        </h3>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Group</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">
                  Description
                </TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {laborGroups.flatMap((g) =>
                g.items.map((item, i) => (
                  <TableRow key={`${g.id}-labor-${i}`} className="text-sm">
                    <TableCell className="font-medium">{g.name}</TableCell>
                    <TableCell>{item.title}</TableCell>
                    <TableCell className="hidden md:table-cell max-w-xs truncate text-xs text-muted-foreground">
                      {item.description}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.quantity ?? ""}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.unitCost ? fmt(item.unitCost) : ""}/hr
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {fmt(item.totalCost)}
                    </TableCell>
                  </TableRow>
                )),
              )}
              <TableRow className="border-t-2 bg-muted/50 font-semibold">
                <TableCell>Labor Total</TableCell>
                <TableCell />
                <TableCell className="hidden md:table-cell" />
                <TableCell />
                <TableCell />
                <TableCell className="text-right tabular-nums">
                  {fmt(laborTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Materials table */}
      <div>
        <h3 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Materials ({fmt(materialTotal)})
        </h3>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="hidden md:table-cell">
                  Cost Code
                </TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {materialGroups.flatMap((g) =>
                g.items.map((item, i) => (
                  <TableRow key={`${g.id}-mat-${i}`} className="text-sm">
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">
                      {item.costCode}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {fmt(item.totalCost)}
                    </TableCell>
                  </TableRow>
                )),
              )}
              <TableRow className="border-t-2 bg-muted/50 font-semibold">
                <TableCell>Material Total</TableCell>
                <TableCell className="hidden md:table-cell" />
                <TableCell className="text-right tabular-nums">
                  {fmt(materialTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// ─── Chat Pane ───────────────────────────────────────────────────────────────

const MOCK_RESPONSES = [
  "The largest single subcontractor cost is Painting by Eddie Perez at $245,100. This covers the first floor interior and the complete exterior of the building.",
  "The framing by JF Ohlund ($221,400) and foundation/concrete ($202,100) are the two biggest structural costs. Together they represent about 20% of the subcontractor budget.",
  "JPL's own labor totals $480,170 across all trades. The largest labor categories are Interior Finish Carpentry ($81,000) and Plumbing Rough ($78,000).",
  "The markup structure is 35% on JPL materials ($29,750) and 15.5% on subcontractor costs ($334,885). There's no markup on JPL's own labor.",
  "The Cabinet Maker (The Brand) is providing all first-floor cabinetry for $177,381. JPL's installation labor adds another $45,120 for a total cabinet cost of about $222,500.",
];

function ChatPane({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const responseIdx = useRef(0);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = input.trim();
      if (!text || isThinking) return;

      const userMsg: ChatMessage = {
        id: String(Date.now()),
        role: "user",
        text,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsThinking(true);

      // Fake response after a short delay
      setTimeout(() => {
        const response =
          MOCK_RESPONSES[responseIdx.current % MOCK_RESPONSES.length]!;
        responseIdx.current++;
        setMessages((prev) => [
          ...prev,
          { id: String(Date.now()), role: "assistant", text: response },
        ]);
        setIsThinking(false);
        // scroll to bottom
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
          });
        });
      }, 800);

      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    },
    [input, isThinking],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="text-sm font-medium">Ask about this estimate</div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-auto p-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              "max-w-[85%] rounded-lg px-3 py-2 text-sm",
              msg.role === "user"
                ? "ml-auto bg-primary text-primary-foreground"
                : "bg-muted",
            )}
          >
            {msg.text}
          </div>
        ))}
        {isThinking && (
          <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            Thinking...
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit} className="border-t p-3">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            className="flex-1"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || isThinking}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
